/**
 * 取引先マスタ(V-・旧: 支払い先マスタ)。一覧・登録・編集・統合・登録依頼の処理(経理・管理者)。
 * 2026-10-23 共通マスタ化: 区分・正式名称・宛名・住所・インボイス番号・支払条件/入金条件、
 * シート用の書き出し(コピー・CSV)と一括取り込み(docs/specs/2026-10-全体設計.md 3章)。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { parseCsv, readCsvFile } from '@core/cash/csv';
import {
  bulkCreateVendors,
  createVendor,
  dismissVendorRequest,
  fetchVendorRequests,
  fetchVendors,
  mergeVendors,
  resolveVendorRequest,
  updateVendor,
} from './vendorRepo';
import TermsEditor from '@core/partners/TermsEditor';
import {
  PARTNER_KIND_OPTIONS,
  isPayable,
  isReceivable,
  kindsOf,
  partnerKindLabel,
  termsLabel,
  termsShortLabel,
} from '@core/partners/terms';
import {
  checkImport,
  parsePastedTable,
  partnerRows,
  toCsv,
  toTsv,
  type ImportRow,
  type PartnerInput,
} from '@core/partners/partnerCsv';
import type { PartnerKind, Vendor, VendorRequest } from '@core/lib/types';

type FormState = PartnerInput & { id: string | null; active: boolean };
type KindFilter = 'all' | PartnerKind;

function emptyForm(): FormState {
  return {
    id: null,
    active: true,
    name: '',
    legalName: '',
    kinds: ['supplier'],
    invoiceAddressee: '',
    postalCode: '',
    address: '',
    invoiceRegNo: '',
    bankInfo: '',
    payTerms: null,
    collectTerms: null,
    note: '',
  };
}

function formOf(v: Vendor): FormState {
  return {
    id: v.id,
    active: v.active,
    name: v.name,
    legalName: v.legal_name ?? '',
    kinds: kindsOf(v),
    invoiceAddressee: v.invoice_addressee ?? '',
    postalCode: v.postal_code ?? '',
    address: v.address ?? '',
    invoiceRegNo: v.invoice_reg_no ?? '',
    bankInfo: v.bank_info ?? '',
    payTerms: v.pay_terms ?? null,
    collectTerms: v.collect_terms ?? null,
    note: v.note ?? '',
  };
}

const STATUS_LABEL: Record<ImportRow['status'], string> = { new: '新規', duplicate: '重複', error: 'エラー' };
const STATUS_BADGE: Record<ImportRow['status'], string> = { new: 'b-ok', duplicate: 'b-pend', error: 'b-ng' };

export default function VendorsTab() {
  const { user } = useAuth();
  const toast = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [requests, setRequests] = useState<VendorRequest[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [mergeSource, setMergeSource] = useState<Vendor | null>(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, r] = await Promise.all([fetchVendors(true), fetchVendorRequests(true)]);
      setVendors(v);
      setRequests(r);
    } catch (e) {
      toast(e instanceof Error ? e.message : '取引先を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(
    () => (kindFilter === 'all' ? vendors : vendors.filter((v) => kindsOf(v).includes(kindFilter))),
    [vendors, kindFilter],
  );

  async function save() {
    if (!form || !user) return;
    if (form.kinds.length === 0) {
      toast('区分を1つ以上選んでください');
      return;
    }
    setBusy(true);
    try {
      if (form.id) {
        await updateVendor(form.id, form);
        toast('取引先を更新しました');
      } else {
        await createVendor(form, user.id);
        toast('取引先を登録しました');
      }
      setForm(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function doMerge() {
    if (!mergeSource || !mergeTarget || !user) return;
    setBusy(true);
    try {
      const result = await mergeVendors({ sourceId: mergeSource.id, targetId: mergeTarget, userId: user.id });
      toast(`「${mergeSource.name}」を統合しました(取引${result.moved}件を付け替え)`);
      setMergeSource(null);
      setMergeTarget('');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '統合に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function approveRequest(r: VendorRequest) {
    if (!user) return;
    setBusy(true);
    try {
      await resolveVendorRequest(r, user.id);
      toast(`「${r.name}」を取引先マスタに登録しました`);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  // --- 書き出し(シートは写し。直すのはこの画面だけ) ---
  async function copyForSheet() {
    const text = toTsv(partnerRows(shown));
    try {
      await navigator.clipboard.writeText(text);
      toast(`${shown.length}件をコピーしました。スプレッドシートの A1 に貼り付けてください`);
    } catch {
      toast('コピーできませんでした。「CSVで書き出し」を使ってください');
    }
  }

  function downloadCsv() {
    const blob = new Blob([toCsv(partnerRows(shown))], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `取引先マスタ_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // --- 一括取り込み ---
  function openImport() {
    setImportText('');
    setImportRows(null);
    setImportOpen(true);
  }

  async function readFile(file: File | undefined) {
    if (!file) return;
    try {
      setImportText(await readCsvFile(file));
      setImportRows(null);
    } catch {
      toast('ファイルを読めませんでした');
    }
  }

  function check() {
    if (!importText.trim()) {
      toast('貼り付けるか、ファイルを選んでください');
      return;
    }
    setImportRows(checkImport(parsePastedTable(importText, parseCsv), vendors));
  }

  const newRows = (importRows ?? []).filter((r) => r.status === 'new' && r.input);

  async function doImport() {
    if (!user || newRows.length === 0) return;
    setBusy(true);
    try {
      const result = await bulkCreateVendors(
        newRows.map((r) => ({ line: r.line, input: r.input as PartnerInput })),
        user.id,
      );
      if (result.failed) {
        toast(`${result.created}件を登録しました。${result.failed.line}行目で止まりました: ${result.failed.message}`);
      } else {
        toast(`${result.created}件を登録しました`);
        setImportOpen(false);
      }
      await load();
      if (result.failed) setImportRows(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : '取り込みに失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const counts = useMemo(() => {
    const c = { new: 0, duplicate: 0, error: 0 };
    for (const r of importRows ?? []) c[r.status]++;
    return c;
  }, [importRows]);

  return (
    <>
      {requests.length > 0 && (
        <div className="card">
          <h3>登録依頼(未対応 {requests.length}件)</h3>
          <table>
            <thead>
              <tr>
                <th>名称</th>
                <th>補足</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="hint">{r.note ?? ''}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-sm-ok" disabled={busy} onClick={() => void approveRequest(r)}>
                      登録する
                    </button>{' '}
                    <button
                      className="btn-sm-ng"
                      disabled={busy}
                      onClick={() => void dismissVendorRequest(r.id, user?.id ?? '').then(load)}
                    >
                      却下
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          取引先マスタ
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn-line" onClick={() => void copyForSheet()} disabled={loading}>
              シート用にコピー
            </button>
            <button className="btn-line" onClick={downloadCsv} disabled={loading}>
              CSVで書き出し
            </button>
            <button className="btn-line" onClick={openImport} disabled={loading}>
              一括取り込み
            </button>
            <button className="btn-line" onClick={() => setForm(emptyForm())}>
              新規登録
            </button>
          </span>
        </h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          元請け・売上先・下請け・仕入先をここで一元管理します(各システムはこの管理番号で参照)。
          スプレッドシートは写しです。直すときはこの画面で直してください。重複は「統合」で1つにまとめられます。
        </p>

        <div className="chips">
          <button className={`chip${kindFilter === 'all' ? ' on' : ''}`} onClick={() => setKindFilter('all')}>
            すべて({vendors.length})
          </button>
          {PARTNER_KIND_OPTIONS.map((o) => (
            <button
              key={o.value}
              className={`chip${kindFilter === o.value ? ' on' : ''}`}
              onClick={() => setKindFilter(o.value)}
            >
              {o.label}({vendors.filter((v) => kindsOf(v).includes(o.value)).length})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : shown.length === 0 ? (
          <p className="empty">該当する取引先はありません</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>管理番号</th>
                <th>名称</th>
                <th>区分</th>
                <th>インボイス番号</th>
                <th>支払条件</th>
                <th>入金条件</th>
                <th>状態</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => (
                <tr key={v.id}>
                  <td className="hint">{v.code}</td>
                  <td>{v.name}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{kindsOf(v).map(partnerKindLabel).join('・')}</td>
                  <td className="hint">{v.invoice_reg_no ?? '—'}</td>
                  <td className="hint" style={{ whiteSpace: 'nowrap' }} title={termsLabel(v.pay_terms)}>
                    {isPayable(v) ? termsShortLabel(v.pay_terms) : '—'}
                  </td>
                  <td className="hint" style={{ whiteSpace: 'nowrap' }} title={termsLabel(v.collect_terms)}>
                    {isReceivable(v) ? termsShortLabel(v.collect_terms) : '—'}
                  </td>
                  <td>
                    <span className={`badge ${v.active ? 'b-ok' : 'b-ng'}`}>{v.active ? '有効' : '無効'}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="txn-link" onClick={() => setForm(formOf(v))}>
                      編集
                    </button>
                    {v.active && (
                      <button className="txn-link" onClick={() => setMergeSource(v)}>
                        統合
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {form && (
        <Modal onClose={() => setForm(null)} width="min(720px, 94vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>{form.id ? '取引先を編集' : '取引先を登録'}</h3>
          </div>

          <div className="form-grid">
            <div>
              <label>名称(画面に出す呼び名)</label>
              <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label>正式名称(任意)</label>
              <input
                value={form.legalName}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
                placeholder="例)株式会社〇〇"
              />
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label className="hint">区分(複数可)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 4 }}>
              {PARTNER_KIND_OPTIONS.map((o) => {
                const checked = form.kinds.includes(o.value);
                return (
                  <label
                    key={o.value}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, whiteSpace: 'nowrap', width: 'auto', margin: 0, cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      style={{ flexShrink: 0, margin: 0, width: 15, height: 15 }}
                      checked={checked}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          kinds: e.target.checked ? [...form.kinds, o.value] : form.kinds.filter((k) => k !== o.value),
                        })
                      }
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="hint" style={{ margin: '4px 0 0' }}>仕入先には経費の支払先(店舗・サービスなど)も含みます</p>
          </div>

          <h4 style={{ margin: '18px 0 8px' }}>請求書・インボイス</h4>
          <div className="form-grid">
            <div>
              <label>請求書の宛名(任意)</label>
              <input
                value={form.invoiceAddressee}
                onChange={(e) => setForm({ ...form, invoiceAddressee: e.target.value })}
                placeholder="例)株式会社〇〇 御中"
              />
            </div>
            <div>
              <label>インボイス登録番号(任意)</label>
              <input
                value={form.invoiceRegNo}
                onChange={(e) => setForm({ ...form, invoiceRegNo: e.target.value })}
                placeholder="T+13桁"
              />
            </div>
            <div>
              <label>郵便番号(任意)</label>
              <input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
            </div>
            <div>
              <label>住所(任意)</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>

          <h4 style={{ margin: '18px 0 8px' }}>支払(当社 → 相手)</h4>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div>
              <label>振込先(任意)</label>
              <input
                value={form.bankInfo}
                onChange={(e) => setForm({ ...form, bankInfo: e.target.value })}
                placeholder="例)〇〇銀行 △△支店 普通 1234567"
              />
            </div>
            <TermsEditor label="支払条件" value={form.payTerms} onChange={(payTerms) => setForm({ ...form, payTerms })} />
          </div>

          <h4 style={{ margin: '18px 0 8px' }}>入金(相手 → 当社)</h4>
          <TermsEditor
            label="入金条件"
            value={form.collectTerms}
            onChange={(collectTerms) => setForm({ ...form, collectTerms })}
          />

          <div className="form-grid" style={{ marginTop: 14 }}>
            <div>
              <label>備考(任意)</label>
              <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            {form.id && (
              <div>
                <label>状態</label>
                <select
                  value={form.active ? '1' : '0'}
                  onChange={(e) => setForm({ ...form, active: e.target.value === '1' })}
                >
                  <option value="1">有効</option>
                  <option value="0">無効</option>
                </select>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button className="btn-green" onClick={() => void save()} disabled={busy}>
              {busy ? '保存中…' : '保存'}
            </button>
          </div>
        </Modal>
      )}

      {importOpen && (
        <Modal onClose={() => setImportOpen(false)} width="min(860px, 94vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>取引先の一括取り込み</h3>
          </div>
          <p className="hint" style={{ marginBottom: 10 }}>
            「シート用にコピー」「CSVで書き出し」と同じ列で読み込みます(「名称」の列は必須・列の順番は自由)。
            登録されるのは「新規」の行だけです。登録済みの取引先は上書きしません。
          </p>
          <textarea
            value={importText}
            onChange={(e) => {
              setImportText(e.target.value);
              setImportRows(null);
            }}
            placeholder="スプレッドシートの表(見出し行を含む)をここに貼り付け"
            rows={6}
            style={{ width: '100%', fontFamily: 'inherit', padding: 8, border: '1px solid var(--line)', borderRadius: 6 }}
          />
          <div style={{ marginTop: 8 }}>
            <label className="hint">または CSV ファイル(UTF-8・Shift_JIS)</label>
            <input type="file" accept=".csv,text/csv" onChange={(e) => void readFile(e.target.files?.[0])} />
          </div>

          {importRows && (
            <div style={{ marginTop: 14 }}>
              <p style={{ margin: '0 0 8px' }}>
                新規 {counts.new}件 / 重複 {counts.duplicate}件 / エラー {counts.error}件
              </p>
              <table>
                <thead>
                  <tr>
                    <th>行</th>
                    <th>名称</th>
                    <th>区分</th>
                    <th>判定</th>
                    <th>内容</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((r) => (
                    <tr key={r.line}>
                      <td className="hint">{r.line}</td>
                      <td>{r.input?.name ?? '—'}</td>
                      <td>{r.input ? r.input.kinds.map(partnerKindLabel).join('・') : '—'}</td>
                      <td>
                        <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                      </td>
                      <td className="hint">{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="modal-actions">
            <button className="btn-line" onClick={check} disabled={busy}>
              内容を確かめる
            </button>
            <button className="btn-green" onClick={() => void doImport()} disabled={busy || newRows.length === 0}>
              {busy ? '登録中…' : `新規 ${newRows.length}件を登録する`}
            </button>
          </div>
        </Modal>
      )}

      {mergeSource && (
        <Modal onClose={() => setMergeSource(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>取引先を統合</h3>
          </div>
          <p className="hint" style={{ marginBottom: 12 }}>
            「{mergeSource.name}({mergeSource.code})」に紐づく取引をすべて統合先へ付け替え、
            この取引先を無効化します。元に戻す場合は手動での付け替えが必要です。
          </p>
          <label className="hint">統合先</label>
          <select
            style={{ width: '100%', padding: '9px 11px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', marginTop: 4 }}
            value={mergeTarget}
            onChange={(e) => setMergeTarget(e.target.value)}
          >
            <option value="">選択してください</option>
            {vendors
              .filter((v) => v.active && v.id !== mergeSource.id)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}({v.code})
                </option>
              ))}
          </select>
          <div className="modal-actions">
            <button className="btn-green" onClick={() => void doMerge()} disabled={busy || !mergeTarget}>
              {busy ? '統合中…' : '統合する'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
